import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { InboxRowItem } from './types';
import { hashColor, companyInitials, relativeTime, TYPE_META, UnreadBadge } from './InboxCard';
import styles from './inboxRow.module.scss';

type InboxGroupHeaderProps = {
  items: InboxRowItem[];     // sorted newest-first; items[0] is the latest
  counterpart: string;       // the other party's name
  sideLabel?: { labelKey: string; fallback: string };   // which way the conversation runs, in a mixed list
  expanded: boolean;
  onToggle: () => void;      // chevron click — expand/collapse
  onSelect: () => void;      // row click — preview the latest message
  isSelected?: boolean;      // true when this group's latest is the selected item
};

const InboxGroupHeader: FC<InboxGroupHeaderProps> = ({
  items, counterpart, sideLabel, expanded, onToggle, onSelect, isSelected = false,
}) => {
  const [t] = useTranslation('dashboard');
  const latest = items[0];
  const count = latest.groupConvCount ?? items.length;
  const unreadTotal = latest.groupUnreadTotal ?? items.reduce((sum, i) => sum + (i.unreadCount || 0), 0);
  const hasUnread = unreadTotal > 0;

  const avatarColor = hashColor(counterpart || latest.conversationId);
  const initials = companyInitials(counterpart);
  const typeInfo = TYPE_META[latest.messageType] ?? TYPE_META.standardMessage;

  const rowState = isSelected ? styles.rowSelected : (hasUnread ? styles.rowUnread : '');

  return (
    <div
      data-inbox-row={latest.conversationId}
      onClick={onSelect}
      className={`${styles.row} ${rowState}`}
    >
      {/* Expand/collapse chevron — occupies the shared lead column. */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        title={expanded ? t('inbox.group.collapse', 'Fäll ihop') : t('inbox.group.expand', 'Expandera')}
        className={styles.chevron}
      >
        {expanded ? <ExpandMoreIcon style={{ fontSize: 20 }} /> : <ChevronRightIcon style={{ fontSize: 20 }} />}
      </button>

      {/* Avatar — company initials */}
      <div className={styles.avatar} style={{ backgroundColor: avatarColor }}>
        {initials}
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.nameRow}>
          <span className={`${styles.name} ${hasUnread ? styles.nameUnread : ''}`}>
            {counterpart || '—'}
          </span>
          {sideLabel && <span className={styles.sideBadge}>{t(sideLabel.labelKey, sideLabel.fallback)}</span>}
          {/* Conversation count — only show when > 1 */}
          {count > 1 && (
            <span className={styles.countBadge}>{count}</span>
          )}
          {latest.projectName && (
            <span className={styles.projectBadge}>{latest.projectName}</span>
          )}
        </div>

        <div className={styles.detailRow}>
          <span
            className={`${styles.typeBadge} ${styles.typeBadgeInline}`}
            style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}
          >
            {t(typeInfo.labelKey, typeInfo.fallback)}
          </span>
          {latest.title && <span className={styles.area}>{latest.title}</span>}
          {latest.title && latest.preview && <span className={styles.sep}>·</span>}
          <span className={styles.preview}>{latest.preview}</span>
        </div>
      </div>

      {/* Right: aggregate unread badge + timestamp */}
      <div className={styles.right}>
        {hasUnread && <UnreadBadge count={unreadTotal} />}
        <span className={styles.timestamp}>{relativeTime(latest.updatedAt, t('inbox.time.yesterday', 'Igår'))}</span>
      </div>
    </div>
  );
};

export default InboxGroupHeader;
